import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { PageHeader } from '../components/ui/PageHeader';
import { MAHNSTUFE_BADGE, MAHNSTUFE_LABELS as MAHNSTUFE_LABEL } from '../lib/api-labels';
import { MAHNSTUFE_MAX, MAHNSTUFE_VALUES, type Mahnstufe } from '../lib/api-enums';

interface MahnUebersichtRow {
  rateId: string;
  rateNr: number;
  faelligAm: string;
  sollBetragBrutto: string;
  status: 'offen' | 'teilweise';
  mahnstufe: Mahnstufe;
  tageUeberfaellig: number;
  rechnungId: string;
  rechnungsnummer: string | null;
  kundeId: string;
  displayName: string;
  bvId: string | null;
  bvNummer: string | null;
}

const STATUS_LABEL: Record<MahnUebersichtRow['status'], string> = {
  offen: 'Offen',
  teilweise: 'Teilweise bezahlt',
};

export const Route = createFileRoute('/vertraege/mahnungen')({
  beforeLoad: () => requireAccess('/api/v1/vertraege/mahnungen/uebersicht'),
  component: MahnungenPage,
});

function MahnungenPage() {
  const qc = useQueryClient();
  const [showLaufModal, setShowLaufModal] = useState(false);

  const uebersichtQ = useQuery({
    queryKey: ['vertraege', 'mahnungen', 'uebersicht'],
    queryFn: () => api<{ items: MahnUebersichtRow[] }>('/api/v1/vertraege/mahnungen/uebersicht'),
  });

  const mahnenMutation = useMutation({
    mutationFn: async (rateId: string) =>
      api<{
        item: { rateId: string; rateNr: number; alteMahnstufe: number; neueMahnstufe: number };
      }>(`/api/v1/vertraege/raten/${rateId}/mahnen`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vertraege', 'mahnungen', 'uebersicht'] });
    },
  });

  const items = uebersichtQ.data?.items ?? [];

  const counts = items.reduce<Record<number, number>>((acc, r) => {
    acc[r.mahnstufe] = (acc[r.mahnstufe] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <PageHeader
        title="Mahnwesen"
        description="Überfällige Raten aus Quartalsabrechnungen — manuell mahnen oder Bulk-Lauf starten."
        actions={
          <button
            onClick={() => setShowLaufModal(true)}
            className="inline-flex items-center rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
          >
            Mahnlauf starten
          </button>
        }
      />

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        <strong>Hinweis:</strong> Mahn-Mails werden aktuell im DRY-RUN-Modus erzeugt (PDF + Audit
        live, Mail-Versand unterdrückt). Schalter:{' '}
        <code className="font-mono">MAHNUNG_DRY_RUN</code> in{' '}
        <code className="font-mono">secrets/production.enc.yaml</code>.
      </div>

      <div className="flex flex-wrap gap-3 text-sm">
        {MAHNSTUFE_VALUES.map((s) => (
          <span
            key={s}
            className={`inline-flex items-center gap-2 rounded-md border px-2 py-1 ${MAHNSTUFE_BADGE[s]}`}
          >
            {MAHNSTUFE_LABEL[s]}: <strong>{counts[s] ?? 0}</strong>
          </span>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <th className="px-3 py-2">BV-Nr</th>
              <th className="px-3 py-2">Kunde</th>
              <th className="px-3 py-2">Rechnung</th>
              <th className="px-3 py-2 text-right">Rate</th>
              <th className="px-3 py-2 text-right">Soll (brutto)</th>
              <th className="px-3 py-2">Fällig</th>
              <th className="px-3 py-2 text-right">Tage überfällig</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Mahnstufe</th>
              <th className="px-3 py-2 text-right">Aktion</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white text-sm">
            {items.map((r) => (
              <tr key={r.rateId} className="hover:bg-slate-50">
                <td className="whitespace-nowrap px-3 py-2 font-mono text-slate-700">
                  {r.bvNummer ?? '—'}
                </td>
                <td className="px-3 py-2 text-slate-700">{r.displayName}</td>
                <td className="whitespace-nowrap px-3 py-2 font-mono text-slate-700">
                  {r.rechnungsnummer ?? r.rechnungId.slice(0, 8)}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-slate-700">
                  {r.rateNr}/3
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-slate-700">
                  {r.sollBetragBrutto} €
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-slate-700">{r.faelligAm}</td>
                <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-slate-700">
                  {r.tageUeberfaellig}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                  {STATUS_LABEL[r.status]}
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  <span
                    className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-medium ${MAHNSTUFE_BADGE[r.mahnstufe]}`}
                  >
                    {MAHNSTUFE_LABEL[r.mahnstufe]}
                  </span>
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right">
                  <button
                    disabled={r.mahnstufe >= MAHNSTUFE_MAX || mahnenMutation.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Rate ${r.rateNr}/3 (${r.displayName}) auf Mahnstufe ${r.mahnstufe + 1} setzen?`,
                        )
                      ) {
                        mahnenMutation.mutate(r.rateId);
                      }
                    }}
                    className="inline-flex items-center rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Mahnen +1
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && !uebersichtQ.isLoading ? (
              <tr>
                <td colSpan={10} className="px-3 py-8 text-center text-sm text-slate-500">
                  Keine überfälligen Raten — alles im grünen Bereich.
                </td>
              </tr>
            ) : null}
            {uebersichtQ.isLoading ? (
              <tr>
                <td colSpan={10} className="px-3 py-8 text-center text-sm text-slate-500">
                  Lade …
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {mahnenMutation.isError ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          Fehler beim Mahnen: {(mahnenMutation.error as Error).message}
        </div>
      ) : null}

      {showLaufModal ? (
        <MahnLaufModal
          onClose={() => setShowLaufModal(false)}
          onSuccess={() => {
            void qc.invalidateQueries({ queryKey: ['vertraege', 'mahnungen', 'uebersicht'] });
            setShowLaufModal(false);
          }}
        />
      ) : null}
    </div>
  );
}

interface MahnLaufModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

function MahnLaufModal({ onClose, onSuccess }: MahnLaufModalProps) {
  const [stichtag, setStichtag] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [tageVorStufe1, setTageVorStufe1] = useState(7);
  const [tageVorStufe2, setTageVorStufe2] = useState(14);
  const [tageVorStufe3, setTageVorStufe3] = useState(14);
  const [result, setResult] = useState<null | {
    laufId: string;
    stichtag: string;
    anzahlGemahnt: number;
    aufstufungen: { auf_1: number; auf_2: number; auf_3: number };
    fehler: Array<{ rateId: string; grund: string }>;
  }>(null);

  const laufMutation = useMutation({
    mutationFn: async () =>
      api<{
        laufId: string;
        stichtag: string;
        anzahlGemahnt: number;
        aufstufungen: { auf_1: number; auf_2: number; auf_3: number };
        fehler: Array<{ rateId: string; grund: string }>;
      }>('/api/v1/vertraege/mahnungen/run', {
        method: 'POST',
        body: { stichtag, fristen: { tageVorStufe1, tageVorStufe2, tageVorStufe3 } },
      }),
    onSuccess: (r) => {
      setResult(r);
      onSuccess();
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-slate-900">Mahnlauf starten</h2>
        <p className="mt-1 text-sm text-slate-600">
          Erhöht die Mahnstufe aller qualifizierenden Raten zum Stichtag. Idempotent — eine Rate die
          heute auf Stufe 1 gehoben wurde qualifiziert sich erst nach „Tage vor Stufe 2" wieder.
        </p>

        <div className="mt-4 space-y-3 text-sm">
          <label className="block">
            <span className="text-slate-700">Stichtag</span>
            <input
              type="date"
              value={stichtag}
              onChange={(e) => setStichtag(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5"
            />
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-slate-700">Tage vor Stufe 1</span>
              <input
                type="number"
                min={0}
                value={tageVorStufe1}
                onChange={(e) => setTageVorStufe1(Number(e.target.value))}
                className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5"
              />
            </label>
            <label className="block">
              <span className="text-slate-700">Tage vor Stufe 2</span>
              <input
                type="number"
                min={0}
                value={tageVorStufe2}
                onChange={(e) => setTageVorStufe2(Number(e.target.value))}
                className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5"
              />
            </label>
            <label className="block">
              <span className="text-slate-700">Tage vor Stufe 3</span>
              <input
                type="number"
                min={0}
                value={tageVorStufe3}
                onChange={(e) => setTageVorStufe3(Number(e.target.value))}
                className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5"
              />
            </label>
          </div>
        </div>

        {result ? (
          <div className="mt-4 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900">
            <div className="font-medium">Lauf abgeschlossen.</div>
            <div className="mt-1">
              Stichtag: {result.stichtag} — gemahnt: <strong>{result.anzahlGemahnt}</strong> (auf
              Stufe 1: {result.aufstufungen.auf_1}, Stufe 2: {result.aufstufungen.auf_2}, Stufe 3:{' '}
              {result.aufstufungen.auf_3})
            </div>
            {result.fehler.length > 0 ? (
              <div className="mt-1 text-amber-900">
                {result.fehler.length} Fehler — siehe Audit-Log (Lauf-Id {result.laufId}).
              </div>
            ) : null}
          </div>
        ) : null}

        {laufMutation.isError ? (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
            Fehler: {(laufMutation.error as Error).message}
          </div>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Schließen
          </button>
          <button
            disabled={laufMutation.isPending || result !== null}
            onClick={() => laufMutation.mutate()}
            className="inline-flex items-center rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {laufMutation.isPending ? 'Läuft …' : 'Lauf starten'}
          </button>
        </div>
      </div>
    </div>
  );
}
